import { useAuth } from '@/App';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tag } from 'lucide-react';

export function PriceBreakdown({
  property, nights, total, deposit, depositLabel, appliedCoupon,
  couponCode, onCouponCodeChange, onValidateCoupon, validatingCoupon,
}) {
  const { isAuthenticated } = useAuth();
  const { formatPrice, t } = useI18n();

  if (!(nights > 0 && property.price_min_usd)) return null;

  const depositText = t('detail.deposit');

  return (
    <div className="mb-6 p-4 bg-[#F5F4F0] rounded-lg space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-[#57534E]">{formatPrice(property.price_min_usd)} x {nights} {t('detail.nights')}</span>
        <span className="text-[#1C1917]">{formatPrice(total)}</span>
      </div>
      {property.cleaning_fee && (
        <div className="flex justify-between text-sm">
          <span className="text-[#57534E]">{t('detail.cleaning')}</span>
          <span className="text-[#1C1917]">{formatPrice(property.cleaning_fee)}</span>
        </div>
      )}
      <div className="border-t border-[#E7E5E4] pt-2 mt-2">
        <div className="flex justify-between font-semibold">
          <span className="text-[#1C1917]">{t('detail.total')}</span>
          <span className="text-[#1C1917]">{formatPrice(total)}</span>
        </div>
        <div className="flex justify-between text-sm text-[#C36F4D] mt-1">
          <span>{depositText}</span>
          <span>{formatPrice(appliedCoupon ? appliedCoupon.original_deposit : deposit)}</span>
        </div>
        {appliedCoupon && (
          <>
            <div className="flex justify-between text-sm text-green-600 mt-1">
              <span>{t('detail.discount')} ({appliedCoupon.code})</span>
              <span>-{formatPrice(appliedCoupon.discount_amount)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold text-[#264639] mt-1">
              <span>{t('detail.depositAfterDiscount')}</span>
              <span>{formatPrice(appliedCoupon.final_deposit)}</span>
            </div>
          </>
        )}
      </div>

      {/* Coupon Code */}
      {isAuthenticated && (
        <CouponInput
          couponCode={couponCode}
          onCouponCodeChange={onCouponCodeChange}
          onValidateCoupon={onValidateCoupon}
          validatingCoupon={validatingCoupon}
          appliedCoupon={appliedCoupon}
          t={t}
        />
      )}
    </div>
  );
}

function CouponInput({ couponCode, onCouponCodeChange, onValidateCoupon, validatingCoupon, appliedCoupon, t }) {
  return (
    <div className="border-t border-[#E7E5E4] pt-3 mt-3">
      <label className="text-xs font-medium text-[#57534E] mb-1 block">{t('detail.coupon')}</label>
      <div className="flex gap-2">
        <Input
          value={couponCode}
          onChange={(e) => onCouponCodeChange(e.target.value.toUpperCase())}
          placeholder="CÓDIGO"
          className="font-mono text-sm flex-1"
          data-testid="coupon-input"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={onValidateCoupon}
          disabled={validatingCoupon || !couponCode}
          className="border-[#264639] text-[#264639] text-xs"
          data-testid="apply-coupon-btn"
        >
          {validatingCoupon ? '...' : 'Aplicar'}
        </Button>
      </div>
      {appliedCoupon && (
        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <Tag className="w-3 h-3" /> {appliedCoupon.description || 'Descuento aplicado'}
        </p>
      )}
    </div>
  );
}
